import { FeaturesI18N } from './features-i18n';
import { UTCDate } from 'types';

export function getTestClassPrefix(): string {
  return 'inforser-webid-test--';
}

export function getTestClassName(path: string): string {
  return getTestClassPrefix() + path.replace(/\./gi, '-').replace(/\[/gi, '-').replace(/\]/gi, '-');
}

export type ArrayColumnSetter<T> = () => { caption: string; func: (record: T, features: any) => void };

export enum DocumentField {
  Photo,
  PhotoHash,
  PassportType,
  PassportSubType,
  Country,
  LastName,
  FirstName,
  SerialNumber,
  Nationality,
  BirthDate,
  ExpiryDate,
  Gender,
}

export class MaskedDate {
  value: string = '';

  static deserialize(template: MaskedDate, source: any): MaskedDate {
    template.value = source.value;
    return template;
  }

  toUTCDate(roundUp?: boolean): UTCDate | null {
    return UTCDate.iso(this.value, roundUp);
  }
}

export class NullableMaskedDate {
  value: string | null = '';

  static deserialize(template: NullableMaskedDate, source: any): NullableMaskedDate {
    template.value = source.value;
    return template;
  }

  toUTCDate(): UTCDate | null {
    return UTCDate.iso(this.value);
  }
}

export class ImageBlob {
  scanned: boolean = false;
  hash: string = '';

  static deserialize(template: ImageBlob, source: any): ImageBlob {
    template.scanned = source.scanned;
    template.hash = source.hash;
    return template;
  }
}

export class Enum {
  private value: string;

  constructor(value: string = '') {
    this.value = value;
  }

  /** Идентификатор перечисления
   * @returns например, для new EnStatus("500") вернет "500"
   * */
  val(): string {
    return this.value;
  }

  /** Идентификатор перечисления как число
   * @returns например, для new EnStatus("500") вернет 500;
   * @throws "Bogus enum value", если значение перечисления нельзя преобразовать в число
   * */
  toNumber() {
    if (this.value) {
      const result = Number(this.value);
      if (!isNaN(result)) {
        return result;
      }
    }
    throw 'Bogus enum value: ' + JSON.stringify(this.value);
  }

  /** Словесное описание значения перечисления с учетом локализации
   * @returns например, для new EnStatus("500") вернет "Данные введены"
   * */
  toString(): string {
    const result = (this as any).constructor.unwrap(this);
    if (result) {
      return result;
    }
    return '';
  }

  /**
   * Определяет, равны ли два экземпляра класса
   * @param other
   * @returns
   */
  equals(other: Enum): boolean {
    return other != null ? (this.val() === other.val()) : false;
  }

  private static resource(key: any): string {
    const resourceKey = 'ENUM.' + this.name + '.' + (this as any)[key].value;
    const resourceValue = FeaturesI18N.getString(resourceKey);
    return resourceValue;
  }

  static wrap(title: string): Enum {
    for (const key in this) {
      if (this.resource(key) === title) {
        return (this as any)[key];
      }
    }
    if (title === '') {
      // По соглашению пустая строка соответствует пустому объекту (null object)
      const res = new (this as any).prototype.constructor('');
      return res;
    }
    throw 'Bogus enum title: ' + JSON.stringify(title);
  }

  static unwrap(object: Enum): string | null {
    for (const key in this) {
      if ((this as any)[key].value === object.value) {
        return this.resource(key);
      }
    }
    return null;
  }

  static deserialize(template: any, source: any): any {
    const title = template.constructor.unwrap(new Enum(source.value));
    if (title === null) {
      return template;
    }
    return template.constructor.wrap(title);
  }

  /** Значение Enum по идентификатору
   * @param val - идентификатор перечисления
   * @returns например, для val = "500" или val = 500 вернет EnStatus.RequestData
   * @throws Error, если отстутсвует значнеие перечиления с идентификатором val
   * */
  static fromValue(val: string | number) {
    for (const key in this) {
      const currentEnumMember = (this as any)[key] as Enum;
      if (currentEnumMember.val() === val.toString()) {
        return currentEnumMember;
      }
    }
    throw new Error(`no value ${val} in enum ${this.name}`);
  }
}
